/* Pure helpers for the LT-1 Tender Estimate.
   Mirrors the Python compute_estimate so the UI can show instant totals
   between debounced saves. The server is still authoritative on save. */

import { v4 as uuidv4 } from "uuid";

const f = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const newId = () => uuidv4();

export function computeLine(line) {
  // LT-38 — missing qty = rate-only line (1); an explicit 0 is 0.
  const qty = line.qty === null || line.qty === undefined || line.qty === "" ? 1 : f(line.qty);
  const hours = f(line.hours);
  const hr = f(line.hourly_rate);
  const matRate = f(line.material_rate);
  const plantRate = f(line.plant_rate);
  const subbyRate = f(line.subby_rate);

  const primaryLabour = hours * hr;
  let extraLabour = 0;
  let extraHours = 0;
  for (const t of line.additional_trades || []) {
    extraLabour += f(t.hours) * f(t.hourly_rate);
    extraHours += f(t.hours);
  }
  const labour_total = primaryLabour + extraLabour;
  const material_total = qty * matRate;
  const plant_total = qty * plantRate;
  const subby_total = qty * subbyRate;
  const line_total = labour_total + material_total + plant_total + subby_total;
  const total_rate = qty ? line_total / qty : line_total;
  return {
    ...line,
    labour_total: round2(labour_total),
    material_total: round2(material_total),
    plant_total: round2(plant_total),
    subby_total: round2(subby_total),
    line_total: round2(line_total),
    total_rate: round2(total_rate),
    _total_hours: round2(hours + extraHours)
  };
}

export function computeEstimate(phases, prelims = 0, oapPct = 22, provsums = 0) {
  let cost = 0;
  let lines = 0;
  let hours = 0;
  const out = (phases || []).map((p, pIdx) => {
    let pHours = 0, pMat = 0, pLab = 0, pPlant = 0, pSub = 0, pTotal = 0;
    let lineSeq = 0;

    const direct = (p.line_items || []).map((li) => {
      lineSeq += 1;
      const c = computeLine(li);
      c.ref = `${pIdx + 1}.${String(lineSeq).padStart(2, "0")}`;
      pHours += c._total_hours;
      pMat += c.material_total;
      pLab += c.labour_total;
      pPlant += c.plant_total;
      pSub += c.subby_total;
      pTotal += c.line_total;
      lines += 1;
      delete c._total_hours;
      return c;
    });

    const subs = (p.sub_phases || []).map((sp, spIdx) => ({
      ...sp,
      sequence: spIdx + 1,
      line_items: (sp.line_items || []).map((li) => {
        lineSeq += 1;
        const c = computeLine(li);
        c.ref = `${pIdx + 1}.${String(lineSeq).padStart(2, "0")}`;
        pHours += c._total_hours;
        pMat += c.material_total;
        pLab += c.labour_total;
        pPlant += c.plant_total;
        pSub += c.subby_total;
        pTotal += c.line_total;
        lines += 1;
        delete c._total_hours;
        return c;
      })
    }));

    cost += pTotal;
    hours += pHours;
    return {
      ...p,
      sequence: pIdx + 1,
      line_items: direct,
      sub_phases: subs,
      hours_total: round2(pHours),
      materials_total: round2(pMat),
      labour_total: round2(pLab),
      plant_total: round2(pPlant),
      subby_total: round2(pSub),
      phase_total: round2(pTotal),
      line_count: lineSeq
    };
  });

  const cost_basis = round2(cost);
  const prelims_total = round2(prelims);
  const provsums_total = round2(provsums);
  const oap_pct = f(oapPct);
  const oap_total = round2((cost_basis + prelims_total + provsums_total) * (oap_pct / 100));
  const client_total = round2(cost_basis + prelims_total + provsums_total + oap_total);
  return {
    phases: out,
    cost_basis,
    prelims_total,
    provsums_total,
    oap_pct,
    oap_total,
    client_total,
    line_items_total: lines,
    hours_total: round2(hours)
  };
}

function round2(n) { return Math.round(n * 100) / 100; }

export function makeBlankLine() {
  return {
    id: newId(),
    description: "",
    type: "adhoc",
    library_item_id: null,
    trade_name: "",
    hours: null, hourly_rate: null,
    qty: null, unit: "nr",
    material_rate: null, plant_rate: null, subby_rate: null,
    hours_override: false,
    hourly_rate_override: false,
    material_rate_override: false,
    plant_rate_override: false,
    subby_rate_override: false,
    additional_trades: [],
    // Lines created from scratch in the estimate (not from library) are
    // implicitly manual. The 3-dot menu lets the user clear this if desired.
    manual_override: true,
    notes: ""
  };
}

export function makeBlankSubPhase(name = "Sub-phase") {
  // Section 3 — auto-insert a single blank line so the user has somewhere to
  // type immediately. Cuts an extra "Add line" click on the most common flow.
  return { id: newId(), name, sequence: 1, line_items: [makeBlankLine()] };
}

export function makeBlankPhase(name) {
  // Section 3 — same treatment: a freshly-added phase from the +Add phase
  // modal lands with one blank line ready to edit. Phases applied via a
  // template are seeded server-side (see /app/backend/utils.py
  // `seed_phase_blank_lines`).
  return {
    id: newId(),
    name: name || "New phase",
    sequence: 1,
    is_collapsed: false,
    sub_phases: [],
    line_items: [makeBlankLine()],
    hours_total: 0, materials_total: 0, labour_total: 0,
    plant_total: 0, subby_total: 0, phase_total: 0, line_count: 0
  };
}
